import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { AuthSession } from "../types/auth.types";
import type { BaseUserResponse } from "../../data/base-user-response.data";
import type { AuthSessionState } from "../store/auth-session-state";
import { authStorage } from "../storage/auth-storage";
import { hasValidOrganizationIdClaim } from "../utils/access-token";
import { AUTH_SESSION_EXPIRED_EVENT } from "../../../../lib/authTokenService";
import {
  MERCHANT_ONBOARDING_STAGES,
  type MerchantOnboardingStage,
} from "../types/merchant-onboarding.types";

interface AuthSessionContextValue extends AuthSessionState {
  setSession: (session: AuthSession) => void;
  clearSession: () => void;
  updateUser: (user: BaseUserResponse) => void;
  updateTokens: (token: string, refreshToken: string) => void;
  updateMerchantOnboarding: (
    stage: MerchantOnboardingStage,
    isOnboardingCompleted: boolean,
  ) => void;
}

const AuthSessionContext = createContext<AuthSessionContextValue | null>(null);

/**
 * Provides the persisted authentication session to the application tree.
 *
 * The provider keeps React state and auth storage synchronized and clears the
 * in-memory session when the shared HTTP client reports an expired session.
 */
export const AuthSessionProvider = ({ children }: { children: React.ReactNode }) => {
  const [currentSession, setCurrentSession] = useState<AuthSession | null>(() =>
    authStorage.load(),
  );

  useEffect(() => {
    const handleSessionExpired = () => setCurrentSession(null);
    window.addEventListener(AUTH_SESSION_EXPIRED_EVENT, handleSessionExpired);
    return () => window.removeEventListener(AUTH_SESSION_EXPIRED_EVENT, handleSessionExpired);
  }, []);

  const contextValue = useMemo<AuthSessionContextValue>(() => {
    const state: AuthSessionState = {
      user: currentSession?.user ?? null,
      token: currentSession?.token ?? null,
      refreshToken: currentSession?.refreshToken ?? null,
      merchantOnboardingStage: currentSession?.merchantOnboardingStage,
      isLoggedIn: !!currentSession?.user,
      isInitialized: true,
    };

    return {
      ...state,
      setSession: (nextSession) => {
        if (!hasValidOrganizationIdClaim(nextSession.token)) {
          authStorage.clear();
          setCurrentSession(null);
          throw new Error("Login failed: Access token is missing a valid organization ID");
        }

        authStorage.save(nextSession);
        setCurrentSession(nextSession);
      },
      clearSession: () => {
        authStorage.clear();
        setCurrentSession(null);
      },
      updateUser: (user) => {
        if (!currentSession) return;
        const merchantOnboardingStage =
          user.role === "merchant" && user.is_onboarding_completed === true
            ? MERCHANT_ONBOARDING_STAGES.completed
            : currentSession.merchantOnboardingStage;
        const nextSession = { ...currentSession, user, merchantOnboardingStage };
        authStorage.save(nextSession);
        setCurrentSession(nextSession);
      },
      updateTokens: (token, refreshToken) => {
        if (!currentSession) return;
        const nextSession = { ...currentSession, token, refreshToken };
        authStorage.save(nextSession);
        setCurrentSession(nextSession);
      },
      updateMerchantOnboarding: (merchantOnboardingStage, isOnboardingCompleted) => {
        if (!currentSession) return;
        const nextSession = {
          ...currentSession,
          user: {
            ...currentSession.user,
            is_onboarding_completed: isOnboardingCompleted,
          },
          merchantOnboardingStage,
        };
        authStorage.save(nextSession);
        setCurrentSession(nextSession);
      },
    };
  }, [currentSession]);

  return <AuthSessionContext.Provider value={contextValue}>{children}</AuthSessionContext.Provider>;
};

/** Returns the current auth session and actions for updating or clearing it. */
export const useAuthSession = () => {
  const context = useContext(AuthSessionContext);
  if (!context) throw new Error("useAuthSession must be used within AuthSessionProvider");

  return {
    ...context,
    logout: context.clearSession,
    initialize: () => undefined,
  };
};
